import { Box, LinearProgress, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { isStructuredExtractedFields } from "@/modules/zoom-calls/types";
import StructuredExtractedFields from "./structured-extracted-fields/structured-extracted-fields";
import NotesCard from "./structured-extracted-fields/notes-card";
import { notesHasContent } from "./structured-extracted-fields/extracted-fields.helpers";
import { formatLabel, isEmpty, isHiddenTechnicalKey } from "./analysis.helpers";

interface ComprehensiveAnalysisProps {
  flags?: Record<string, any> | null;
  sentiment?: Record<string, any> | null;
  extractedFields?: Record<string, any> | null;
  extractedFieldsV2?: Record<string, any> | null;
  /** Render the "Call details" + "Talk ratio" cards (default true). */
  showCallInsights?: boolean;
  /** Render the extracted-fields section (default true). */
  showExtractedFields?: boolean;
  /** Render the "Notes" card (top-level structured flags); defaults to follow showCallInsights. */
  showNotes?: boolean;
}

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.map((x) => formatValue(x)).join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "string") return formatLabel(v);
  return String(v);
};

const toPercent = (n: unknown): number | null => {
  // Guard null/undefined/"" explicitly — Number(null) and Number("") are 0, which
  // would otherwise surface a bogus "0%" and keep the Talk ratio card visible.
  if (n === null || n === undefined || n === "") return null;
  const num = typeof n === "number" ? n : Number(n);
  if (!isFinite(num)) return null;
  return num <= 1 ? Math.round(num * 100) : Math.round(num);
};

const ComprehensiveAnalysis = ({
  flags,
  sentiment,
  extractedFields,
  extractedFieldsV2,
  showCallInsights = true,
  showExtractedFields = true,
  showNotes,
}: ComprehensiveAnalysisProps) => {
  const theme = useTheme();
  const p = theme.nawy.colors.primitives;

  const callType = flags?.call_type as string | undefined;
  const language = flags?.language as string | undefined;
  const clientDialect = flags?.client_dialect as string | undefined;
  const transcriptionQuality = flags?.transcription_quality as
    | string
    | undefined;
  const agentGender = flags?.agent_gender as string | undefined;
  const clientGender = flags?.client_gender as string | undefined;
  const agentTalkRatio = toPercent(flags?.agent_talk_ratio);
  const clientTalkRatio = toPercent(flags?.client_talk_ratio);
  const sentimentArc = sentiment?.arc as string | undefined;

  const detailRows = useMemo(() => {
    const rows: Array<{ label: string; value: string }> = [];
    if (callType)
      rows.push({ label: "Call type", value: formatLabel(callType) });
    if (language)
      rows.push({ label: "Language", value: formatLabel(language) });
    if (clientDialect)
      rows.push({ label: "Client dialect", value: formatLabel(clientDialect) });
    if (transcriptionQuality)
      rows.push({
        label: "Transcription quality",
        value: formatLabel(transcriptionQuality),
      });
    if (agentGender)
      rows.push({ label: "Agent gender", value: formatLabel(agentGender) });
    if (clientGender)
      rows.push({ label: "Client gender", value: formatLabel(clientGender) });
    return rows;
  }, [
    callType,
    language,
    clientDialect,
    transcriptionQuality,
    agentGender,
    clientGender,
  ]);

  // The new structured shape (nested requirements/pitches) gets a dedicated view. It
  // arrives in its own `extractedFieldsV2` field; we still fall back to a structured
  // `extractedFields` for backward compatibility. The legacy flat key-value map keeps
  // the generic grid below.
  const structured = isStructuredExtractedFields(extractedFieldsV2)
    ? extractedFieldsV2
    : isStructuredExtractedFields(extractedFields)
      ? extractedFields
      : null;

  const extractedEntries = useMemo(
    () =>
      structured
        ? []
        : Object.entries(extractedFields ?? {}).filter(
            ([key, value]) => !isHiddenTechnicalKey(key) && !isEmpty(value),
          ),
    [extractedFields, structured],
  );

  // Each section renders only when its toggle is on AND it actually carries data,
  // so a response with nothing for a section produces no empty header/card.
  const hasDetails = detailRows.length > 0;
  const hasArc = Boolean(sentimentArc);
  const hasTalk = agentTalkRatio !== null || clientTalkRatio !== null;
  const hasExtracted = structured
    ? Array.isArray(structured.requirements) &&
      structured.requirements.length > 0
    : extractedEntries.length > 0;
  const hasNotes = notesHasContent(structured);

  const showDetails = showCallInsights && hasDetails;
  const showArc = showCallInsights && hasArc;
  const showTalk = showCallInsights && hasTalk;
  // Notes belong to the Analysis view — default to following the call-insights toggle.
  const shouldShowNotes = (showNotes ?? showCallInsights) && hasNotes;
  const showExtractedSection = showExtractedFields && hasExtracted;
  const hasAnyContent =
    showDetails ||
    showArc ||
    showTalk ||
    showExtractedSection ||
    shouldShowNotes;

  const sectionLabelSx = {
    fontSize: 11,
    fontWeight: 400,
    lineHeight: "16px",
    color: theme.nawy.colors.semantic.text.tertiary,
    whiteSpace: "nowrap" as const,
    mb: "8px",
  };

  const cardSx = {
    p: "12px",
    borderRadius: "12px",
    backgroundColor: theme.nawy.colors.semantic.layer.secondary,
  };

  if (!hasAnyContent) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.sm,
      }}
    >
      {showDetails && (
        <Box sx={cardSx}>
          <Typography sx={sectionLabelSx}>Call details</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              columnGap: "16px",
              rowGap: "6px",
            }}
          >
            {detailRows.map((row) => (
              <Box key={row.label} sx={{ display: "contents" }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: p.grey[700],
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: p.navy[800],
                    textAlign: "left",
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {showArc && (
        <Box sx={cardSx}>
          <Typography sx={sectionLabelSx}>Sentiment arc</Typography>
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, color: p.navy[800] }}
          >
            {formatLabel(sentimentArc as string)}
          </Typography>
        </Box>
      )}

      {showTalk && (
        <Box sx={cardSx}>
          <Typography sx={sectionLabelSx}>Talk ratio</Typography>
          {agentTalkRatio !== null && (
            <Box sx={{ mb: clientTalkRatio !== null ? "8px" : 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: "4px",
                }}
              >
                <Typography sx={{ fontSize: 12, color: p.grey[700] }}>
                  Agent
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: p.navy[800],
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {agentTalkRatio}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={agentTalkRatio}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: p.navy[50],
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: p.blue[500],
                  },
                }}
              />
            </Box>
          )}
          {clientTalkRatio !== null && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: "4px",
                }}
              >
                <Typography sx={{ fontSize: 12, color: p.grey[700] }}>
                  Client
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: p.navy[800],
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {clientTalkRatio}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={clientTalkRatio}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: p.navy[50],
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: p.violet[500],
                  },
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {shouldShowNotes && <NotesCard data={structured} />}

      {showExtractedSection &&
        (structured ? (
          <StructuredExtractedFields data={structured} />
        ) : (
          <Box sx={cardSx}>
            <Typography sx={sectionLabelSx}>Extracted fields</Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: "16px",
                rowGap: "6px",
              }}
            >
              {extractedEntries.map(([key, value]) => (
                <Box key={key} sx={{ display: "contents" }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: p.grey[700],
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatLabel(key)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: p.navy[800],
                      textAlign: "right",
                      wordBreak: "break-word",
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
    </Box>
  );
};

export default ComprehensiveAnalysis;
